import * as React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { Toaster } from 'sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/features/auth/AuthContext';
import { CurrencyProvider } from '@/features/currencies/CurrencyProvider';
import { OnboardingProvider } from '@/features/onboarding/OnboardingProvider';
import { createQueryClient } from './queryClient';
import { useUiStore } from '@/stores/ui.store';
import i18n, { applyLocale, dirFor } from '@/lib/i18n';

/** Applies theme and locale to `<html>`. `index.html` does the same before first paint. */
const DocumentSettings = () => {
  const theme = useUiStore((state) => state.theme);
  const locale = useUiStore((state) => state.locale);

  React.useEffect(() => {
    const root = document.documentElement;

    const apply = () => {
      const dark =
        theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
      root.classList.toggle('dark', dark);
    };

    apply();

    // Only 'system' cares about the OS flipping mid-session.
    if (theme !== 'system') return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [theme]);

  React.useEffect(() => {
    applyLocale(locale);
  }, [locale]);

  return null;
};

/**
 * Provider order matters:
 * QueryClient → i18n → Auth → Currency → Onboarding → Tooltip.
 *
 * `AuthProvider` calls `useQueryClient` to clear cached server data on sign-out, so it
 * must sit inside the query provider. `CurrencyProvider` sits inside `AuthProvider`
 * because its list is an authenticated read, and outside everything that renders an
 * amount because it is what teaches `money.js` each currency's decimals before the first
 * figure is drawn. `OnboardingProvider` needs the user, so it comes after auth.
 */
export const Providers = ({ children, queryClient }) => {
  const [client] = React.useState(() => queryClient ?? createQueryClient());
  const locale = useUiStore((state) => state.locale);

  return (
    <QueryClientProvider client={client}>
      <I18nextProvider i18n={i18n}>
        <AuthProvider>
          <CurrencyProvider>
            <OnboardingProvider>
              <TooltipProvider delayDuration={200}>
                <DocumentSettings />
                {children}
                {/* Bottom-right in English, bottom-left in Arabic (docs/04 §4). */}
                <Toaster
                  position={dirFor(locale) === 'rtl' ? 'bottom-left' : 'bottom-right'}
                  dir={dirFor(locale)}
                  duration={3000}
                  closeButton
                  richColors
                />
              </TooltipProvider>
            </OnboardingProvider>
          </CurrencyProvider>
        </AuthProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
};

export default Providers;
